import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import type { ProvisioningLogEntry } from "@/lib/types";

export function useProvisioningLogs(targetId: number, enabled: boolean) {
  const [logs, setLogs] = useState<ProvisioningLogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const retryCountRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      setLogs([]);
      setError(null);
      retryCountRef.current = 0;
      return;
    }

    // Fetch historical logs first
    api
      .getProvisioningLogs(targetId)
      .then((entries) => {
        setLogs(entries);
        setError(null);
      })
      .catch((fetchError) => {
        console.error("Failed to fetch provisioning logs:", fetchError);
        setError(`Failed to load logs: ${fetchError.message}`);
      });

    // Then open SSE stream for real-time updates
    const source = new EventSource(`/api/spot-targets/${targetId}/logs/stream`);

    source.onmessage = (event) => {
      try {
        const entry: ProvisioningLogEntry = JSON.parse(event.data);
        setLogs((previous) => [...previous, entry]);
        setError(null);
        retryCountRef.current = 0;
      } catch (parseError) {
        console.error("Failed to parse SSE message:", parseError, event.data);
      }
    };

    source.onerror = () => {
      retryCountRef.current += 1;
      // EventSource auto-reconnects; surface error after repeated failures
      if (retryCountRef.current >= 3) {
        setError("Live log connection lost. Retrying...");
      }
    };

    source.onopen = () => {
      retryCountRef.current = 0;
      setError(null);
    };

    return () => source.close();
  }, [targetId, enabled]);

  return { logs, error };
}
